import React, { useEffect, useState } from "react";
import { apiClient } from "../api_client";
import * as Icons from "./SVGs";
import { START_TYPES, FINISH_TYPES } from "../constants/race";

const getKeyByValue = (obj, value) =>
  Object.keys(obj).find((key) => obj[key] === value)?.toLowerCase();

export default function EventControls({ id, onSuccess }) {
  const [controls, setControls] = useState(null);
  const [savedControls, setSavedControls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checkpointCount, setCheckpointCount] = useState(0);
  const [checkpointNames, setCheckpointNames] = useState([]);

  useEffect(() => {
    const fetchControls = async () => {
      setLoading(true);
      try {
        const response = await apiClient.eventControls.get(id);
        const initialControls = {
          start_type: getKeyByValue(START_TYPES, response.start_type),
          finish_type: getKeyByValue(FINISH_TYPES, response.finish_type),
          start_equals_finish: response.start_equals_finish,
          checkpoint_required: response.checkpoint_required,
          open: response.open,
          safety_beacons: response.safety_beacons,
          checkpoint_names: response.checkpoints?.map(cp => cp.name) || [],
        };
        setControls(initialControls);
        setSavedControls({
          ...initialControls,
          checkpoint_names: response.checkpoints?.map((cp) => cp.name) || [],
        });

        setCheckpointNames(response.checkpoints?.map((cp) => cp.name) || []);
        setCheckpointCount(response.checkpoints?.length || 0);

        const anyNull = Object.values(initialControls).some((v) => v === null);
        setNeedsSetup(anyNull);
      } catch (err) {
        console.error(err);
        alert("Failed to fetch event controls");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchControls();
  }, [id]);

  const handleChange = (key, value) => {
    setControls((prev) => {
      const next = { ...prev };
      if (key === "start_equals_finish") {
        next.start_equals_finish = value;
        if (value) {
          next.checkpoint_required = true;
        }
      } else if (key === "checkpoint_required") {
        if (prev.start_equals_finish && value === false) {
          return prev;

        } else {
          next.checkpoint_required = value;
        }
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const handleCheckpointCountChange = (count) => {
    setCheckpointCount(count);
    setCheckpointNames((prev) => {
      const newArr = [...prev];
      while (newArr.length < count) newArr.push("");
      return newArr.slice(0, count);
    });
  };

  const handleCheckpointNameChange = (index, name) => {
    setControls((prev) => {
      const next = { ...prev };
      next.checkpoint_names[index] = name;
      return next;
    });
    setCheckpointNames((prev) => {
      const newArr = [...prev];
      newArr[index] = name;
      return newArr;
    });
  };

  const hasChanges =
    JSON.stringify(controls) !== JSON.stringify(savedControls);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!controls) return;
    setSaving(true);

    try {
      const payload = {
        ...controls,
        start_type: START_TYPES[controls.start_type.toUpperCase()],
        finish_type: FINISH_TYPES[controls.finish_type.toUpperCase()],
        checkpoint_names: checkpointNames,
      };
      await apiClient.eventControls.updateControls(id, payload);
      setSavedControls(controls);
      alert("Event controls saved!");
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save event controls");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !controls) return <div>Loading event controls...</div>;

  const disableInputs = !needsSetup && !confirmed;

  const getBorderClass = (isSelected) =>
    isSelected
      ? "border border-warning border-3 rounded p-3 cursor-pointer"
      : "border border-transparent p-3 cursor-pointer";

  return (
    <div>
      {!needsSetup && (
        <div className="alert alert-warning mb-4">
          <strong>Warning:</strong> All beacon options are set.
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="confirmChanges"
              checked={confirmed}
              onChange={() => setConfirmed(!confirmed)}
            />
            <label className="form-check-label" htmlFor="confirmChanges">
              I understand that changes could affect the event.
            </label>
          </div>
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* Start Type */}
        <div className="mb-3 card">
          <h4 className="card-header">Start Type</h4>
          <div className="card-body">
            <p>Choose how the race is started</p>
            <div className="row text-center">
              {[
                { label: "Chasing Start", value: "chasing", Icon: Icons.ChasingStartSVG, description: "Time will be started when the entrant leaves the start pen." },
                { label: "Mass Start", value: "mass", Icon: Icons.MassStartSVG, description: "Time will be started when the organiser starts the race." },
              ].map((option) => (
                <div
                  key={option.value}
                  className={`col-md-6 d-flex flex-column align-items-center ${getBorderClass(controls.start_type === option.value)}`}
                  onClick={() => !disableInputs && handleChange("start_type", option.value)}
                >
                  <label className="form-check-label mb-2" style={{ fontWeight: "bold" }}>
                    {option.label}
                  </label>
                  <input
                    type="radio"
                    name="startType"
                    checked={controls.start_type === option.value}
                    onChange={() => handleChange("start_type", option.value)}
                    style={{ width: "2rem", height: "2rem" }}
                    disabled={disableInputs}
                  />
                  <p className="mt-3 text-center" style={{ maxWidth: "90%" }}>
                    {option.description}
                  </p>
                  <option.Icon width={400} height={200} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Start = Finish */}
        <div className="mb-3 card">
          <h4 className="card-header">Start = Finish</h4>
          <div className="card-body">
            <p>
              The start beacons will be treated as finish after passing a checkpoint. Enable if you want the finish to be at the same location as the start. Disable if the route is likely to loop back to the start area before finishing.
            </p>

            <div className="row text-center mt-3">
              {[
                { label: "Yes", value: true, description: "Use start beacon as finish after passing a checkpoint." },
                { label: "No", value: false, description: "Do not use start beacon as finish." },
              ].map((option) => (
                <div
                  key={String(option.value)}
                  className={`col-md-6 d-flex flex-column align-items-center ${getBorderClass(controls.start_equals_finish === option.value)}`}
                  onClick={() => !disableInputs && handleChange("start_equals_finish", option.value)}
                >
                  <label className="form-check-label mb-2" style={{ fontWeight: "bold" }}>
                    {option.label}
                  </label>
                  <input
                    type="radio"
                    name="startEqualsFinish"
                    checked={controls.start_equals_finish === option.value}
                    onChange={() => handleChange("start_equals_finish", option.value)}
                    style={{ width: "2rem", height: "2rem" }}
                    disabled={disableInputs}
                  />
                  <p className="mt-3 text-center" style={{ maxWidth: "90%" }}>
                    {option.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Checkpoint Requirement */}
        <div className="mb-3 card">
          <h4 className="card-header">Checkpoint Requirement</h4>
          <div className="card-body">
            <p>Whether entrants must pass at least one checkpoint before finishing.</p>
            <div className="row text-center mt-3">
              {[
                { label: "Checkpoints Required", value: true, description: "Entrants must pass at least one checkpoint before finishing." },
                { label: "No Checkpoints", value: false, description: "Entrants can finish without passing any checkpoints." },
              ].map((option) => (
                <div
                  key={String(option.value)}
                  className={`col-md-6 d-flex flex-column align-items-center ${getBorderClass(controls.checkpoint_required === option.value)}`}
                  onClick={() => !disableInputs && handleChange("checkpoint_required", option.value)}
                >
                  <label className="form-check-label mb-2" style={{ fontWeight: "bold" }}>
                    {option.label}
                  </label>
                  <input
                    type="radio"
                    name="checkpointRequired"
                    checked={controls.checkpoint_required === option.value}
                    onChange={() => handleChange("checkpoint_required", option.value)}
                    style={{ width: "2rem", height: "2rem" }}
                    disabled={disableInputs || controls.start_equals_finish}
                  />
                  <p className="mt-3 text-center" style={{ maxWidth: "90%" }}>
                    {option.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Checkpoints Section */}
        <div className="mb-4 card">
          <h4 className="card-header">Checkpoints</h4>
          <div className="card-body">
            <p>How many checkpoints do you want, and what are their names? Leave blank for just numbers.</p>

            <div className="mb-3">
              <input
                type="number"
                min={0}
                max={20}
                value={checkpointCount}
                onChange={(e) => handleCheckpointCountChange(Number(e.target.value))}
                className="form-control"
                style={{ width: "100px" }}
                disabled={disableInputs}
              />
            </div>

            {checkpointNames.map((name, idx) => (
              <div key={idx} className="col-md-6 mb-2 d-flex align-items-center">
                <label htmlFor={`cp-${idx}`} className="me-2 mb-0" style={{ width: "150px" }}>
                  Checkpoint {idx + 1}:
                </label>
                <input
                  id={`cp-${idx}`}
                  type="text"
                  value={name}
                  maxLength={20}
                  size={20}
                  onChange={(e) => handleCheckpointNameChange(idx, e.target.value)}
                  className="form-control"
                  disabled={disableInputs}
                  style={{ width: "22ch" }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Finish Type */}
        <div className="mb-4 card">
          <h4 className="card-header">Finish Type</h4>
          <div className="card-body">
            <p>Choose if the finish line is a checkpoint, a funnel, or combined.</p>
            <div className="row text-center mt-3">
              {[
                { label: "Line", value: "checkpoint", Icon: Icons.CheckpointLineSVG, description: "Entrants go through and continue for at least 30 meters." },
                { label: "Funnel", value: "funnel", Icon: Icons.FinishFunnelSVG, description: "Entrants enter the finish funnel and stay within for at least 20 seconds." },
                { label: "Combined", value: "combined", Icon: Icons.CombinedFinishSVG, description: "Entrants pass a checkpoint and then into a finish zone at least 10m away." },
              ].map((option) => (
                <div
                  key={option.value}
                  className={`col-md-4 d-flex flex-column align-items-center ${getBorderClass(controls.finish_type === option.value)}`}
                  onClick={() => !disableInputs && handleChange("finish_type", option.value)}
                >
                  <label className="form-check-label mb-2" style={{ fontWeight: "bold" }}>
                    {option.label}
                  </label>
                  <input
                    type="radio"
                    name="finishType"
                    checked={controls.finish_type === option.value}
                    onChange={() => handleChange("finish_type", option.value)}
                    style={{ width: "2rem", height: "2rem" }}
                    disabled={disableInputs}
                  />
                  <p className="mt-3 text-center" style={{ maxWidth: "90%" }}>
                    {option.description}
                  </p>
                  <option.Icon width={400} height={200} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Safety beacons */}
        <div className="mb-4 card">
          <h4 className="card-header">Safety Beacons</h4>
          <div className="card-body">
            <p>Choose weather or not you want to add safety beacons at the start and finish for extra certainty on entrant state.</p>
            <div className="row text-center mt-3">
              <div className="col-md-6 d-flex flex-column align-items-center">
                <label className="form-check-label mb-2" htmlFor="safetyBeacons" style={{ fontWeight: "bold" }}>
                  No
                </label>
                <input
                  className="form-check-input"
                  type="radio"
                  name="safetyBeacons"
                  id="safetyBeacons"
                  checked={controls.safety_beacons === false}
                  onChange={() => handleChange("safety_beacons", false)}
                  style={{ width: "2rem", height: "2rem" }}
                  disabled={disableInputs}
                />
                <p className="mt-3 text-center" style={{ maxWidth: "90%" }}>
                  Do not add safety beacons.
                </p>
                <Icons.NoSafetySVG width={400} height={200} />
              </div>

              <div className="col-md-6 d-flex flex-column align-items-center">
                <label className="form-check-label mb-2" htmlFor="safetyBeaconsYes" style={{ fontWeight: "bold" }}>
                  Yes
                </label>
                <input
                  className="form-check-input"
                  type="radio"
                  name="safetyBeacons"
                  id="safetyBeacons"
                  checked={controls.safety_beacons === true}
                  onChange={() => handleChange("safety_beacons", true)}
                  style={{ width: "2rem", height: "2rem" }}
                  disabled={disableInputs}
                />
                <p className="mt-3 text-center" style={{ maxWidth: "90%" }}>
                  Add safety beacons.
                </p>
                <Icons.YesSafetySVG width={400} height={200} />
              </div>
            </div>
          </div>
        </div>

        {/* Event Privacy */}
        <div className="mb-4 card">
          <h4 className="card-header">Event Privacy</h4>
          <div className="card-body">
            <p>Choose if the event should be open or closed.</p>
            <div className="row text-center mt-3">
              {[
                { label: "Closed", value: false, description: "Entrants need to preregister and use their bib number and provided pin to login." },
                { label: "Open", value: true, description: "Entrants can signup and login on the app with just their name." },
              ].map((option) => (
                <div
                  key={String(option.value)}
                  className={`col-md-6 d-flex flex-column align-items-center ${getBorderClass(controls.open === option.value)}`}
                  onClick={() => !disableInputs && handleChange("open", option.value)}
                >
                  <label className="form-check-label mb-2" style={{ fontWeight: "bold" }}>
                    {option.label}
                  </label>
                  <input
                    type="radio"
                    name="privacy"
                    checked={controls.open === option.value}
                    onChange={() => handleChange("open", option.value)}
                    style={{ width: "2rem", height: "2rem" }}
                    disabled={disableInputs}
                  />
                  <p className="mt-3 text-center" style={{ maxWidth: "90%" }}>
                    {option.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        
        <button
          className="btn btn-primary"
          type="submit"
          disabled={!hasChanges || saving || (!confirmed && !needsSetup)}
        >
          {hasChanges ? "Save Changes" : "Saved"}
          <i className="fa-solid fa-floppy-disk ms-2"></i>
        </button>
      </form>
      {onSuccess && (
        <button
          type="button"
          className="btn btn-secondary mt-2"
          onClick={() => onSuccess()}
        >
          Skip for now
        </button>
      )}
    </div>
  );
}
